import React, { useState, useEffect, useMemo, useRef, Suspense, lazy } from 'react';
import {
  Check, X, ArrowRight, Building2, Tags, BarChart3, FileText, ShieldCheck, Sparkles, Minus, Plus, Sun, Moon, Box, Route, Map, Instagram, Youtube, ChevronUp, LayoutDashboard, Circle
} from 'lucide-react';
import { BASE_MONTHLY, BASE_ANNUAL, SEAT_MONTHLY, SEAT_ANNUAL, ANNUAL_SAVINGS_LABEL, TRIAL_DAYS } from '../utils/pricing';
import LegalDocModal from '../components/LegalDocModal';
import ImageLightbox from '../components/ImageLightbox';
import { trackTrialCtaClick, trackDemoStart } from '../utils/analytics';

// lucide-react only ships the old bird logo (exported as `Twitter`) — wrong
// mark for the current X rebrand, so this is the actual X glyph inlined as
// an SVG instead. Named XLogoIcon, not `X`, to avoid colliding with
// lucide's own `X` (the close-icon glyph) already imported above.
function XLogoIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l12 15.644Z" />
    </svg>
  );
}

// lucide-react's `Linkedin` is a generic stroke-outline glyph, not the real
// brand mark — at the footer's small icon size the "in" lettering loses its
// "n" stem entirely. This is the actual filled LinkedIn logo inlined instead.
function LinkedinLogoIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zM7.114 20.452H3.56V9h3.554v11.452z" />
    </svg>
  );
}

// Same reasoning as XLogoIcon — Threads (Meta) is too new for lucide-react
// to ship a mark for, so it's inlined here too.
function ThreadsLogoIcon(props) {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" {...props}>
      <path d="M6.321 6.016c-.27-.18-1.166-.802-1.166-.802.756-1.081 1.753-1.502 3.132-1.502.975 0 1.803.327 2.394.948s.928 1.509 1.005 2.644q.492.207.905.484c1.109.745 1.719 1.86 1.719 3.137 0 2.716-2.226 5.075-6.256 5.075C4.594 16 1 13.987 1 7.994 1 2.034 4.482 0 8.044 0 9.69 0 13.55.243 15 5.036l-1.36.353C12.516 1.974 10.163 1.43 8.006 1.43c-3.565 0-5.582 2.171-5.582 6.79 0 4.143 2.254 6.343 5.63 6.343 2.777 0 4.847-1.443 4.847-3.556 0-1.438-1.208-2.127-1.27-2.127-.236 1.234-.868 3.31-3.644 3.31-1.618 0-3.013-1.118-3.013-2.582 0-2.09 1.984-2.847 3.55-2.847.586 0 1.294.04 1.663.114 0-.637-.54-1.728-1.9-1.728-1.25 0-1.566.405-1.967.868ZM8.716 8.19c-2.04 0-2.304.87-2.304 1.416 0 .878 1.043 1.168 1.6 1.168 1.02 0 2.067-.282 2.232-2.423a6.2 6.2 0 0 0-1.528-.161" />
    </svg>
  );
}

// One entry per confirmed live account — QuoteHaul's LinkedIn, X, Instagram,
// and Threads accounts went live 2026-08-17, YouTube shortly after
// (project memory: project_social_accounts.md); more will be added here as
// they're confirmed, same shape (href + label + icon), rather than each new
// one needing its own bespoke footer markup.
const SOCIAL_LINKS = [
  { href: 'https://www.linkedin.com/showcase/quotehaul', label: 'QuoteHaul on LinkedIn', Icon: LinkedinLogoIcon },
  { href: 'https://x.com/QuoteHaulApp', label: 'QuoteHaul on X', Icon: XLogoIcon },
  { href: 'https://www.instagram.com/quotehaul', label: 'QuoteHaul on Instagram', Icon: Instagram },
  { href: 'https://www.threads.com/@quotehaul', label: 'QuoteHaul on Threads', Icon: ThreadsLogoIcon },
  { href: 'https://www.youtube.com/@QuoteHaulApp', label: 'QuoteHaul on YouTube', Icon: Youtube },
];

// Lazy-loaded: this is the only thing on the whole landing page that pulls
// in real Firebase Auth/Firestore (see TrialFlow.jsx) — the dynamic import()
// only fires once a visitor actually clicks "Start Your Trial," so everyone
// just browsing never downloads it at all.
const TrialFlow = lazy(() => import('./TrialFlow.jsx'));

// Filenames match public/img/marketing/ (trimmed of the source tool's
// leading timestamp prefix). This same folder doubles as the source for any
// future HelpPage.jsx tutorial images too — a separate flat/unstyled
// public/img/tutorials/ used to exist for that, but was removed once it
// became a pure duplicate of this folder's content.
//
// Also the flat source of truth ProductTourLightbox's prev/next cycles
// through — a feature's own `shots` (below) indexes into this array rather
// than duplicating entries, so clicking "next" inside one feature's gallery
// continues into whichever feature comes next in this reading order.
const PRODUCT_TOUR_SHOTS = [
  { file: 'dashboard-overview-stats.webp', title: 'Live Dashboard', desc: 'See your whole book of business at a glance.', alt: 'QuoteHaul dashboard showing shipment stats overview' },
  { file: 'shipment-cargo-visualization.webp', title: '3D Cargo Visualization', desc: 'Know it fits before you quote it — in AR, on device.', alt: 'Interactive 3D cargo visualization inside a trailer' },
  { file: 'multistop-load-cargo-visualization.webp', title: 'Combined Load Visualization', desc: 'Every stop\'s cargo, color-coded in one shared 3D truck view.', alt: '3D visualization of a multi-stop combined truckload' },
  { file: 'freight-analytics-multistop-loads.webp', title: 'Multi-Stop Loads Report', desc: 'See performance across combined, multi-pickup shipments.', alt: 'Multi-stop loads analytics report' },
  { file: 'organizations-directory-list.webp', title: 'Organizations Directory', desc: 'Every shipper, consignee, and carrier, saved once.', alt: 'Organizations directory list' },
  { file: 'rate-cards-management.webp', title: 'Negotiated Rate Cards', desc: 'Your real carrier rates, not a spot-market guess.', alt: 'Rate card management screen' },
  { file: 'trucking-quote-pdf-preview.webp', title: 'Instant Paperwork', desc: 'BOLs, invoices, and quote PDFs generated in one click.', alt: 'Generated freight quote PDF preview' },
  { file: 'shipping-label-barcode.webp', title: 'Shipping Labels', desc: 'Barcoded 4"×6" labels, one per physical piece, generated straight from the shipment.', alt: 'Barcoded 4x6 shipping label generated from a shipment' },
  { file: 'freight-analytics-overview.webp', title: 'Freight Analytics', desc: 'Margin, volume, and win-loss trends, not just one quote.', alt: 'Freight margin and analytics dashboard' },
  { file: 'freight-analytics-shipping-lanes-map.webp', title: 'Shipping Lanes Map', desc: 'Every lane you\'ve quoted, mapped across the country.', alt: 'US map showing shipping lane density between states' },
  { file: 'freight-analytics-shipping-lanes-california-report.webp', title: 'State Lane Report', desc: 'Click any state for its own inbound/outbound breakdown.', alt: 'California state lane report showing inbound and outbound shipment breakdown' },
  { file: 'settings-team-user-management.webp', title: 'Team & User Management', desc: 'Seats, roles, and admin controls for your whole team.', alt: 'Team and user management settings screen' },
  { file: 'admin-tasks-review-queue.webp', title: 'Admin Approvals', desc: 'Deleting an approved or voided record requires admin sign-off, tracked start to finish.', alt: 'Admin task queue reviewing a pending approval request' },
  { file: 'my-requests-pending-approval.webp', title: 'My Requests', desc: 'Every teammate can track the status of what they\'ve submitted.', alt: 'My Requests page showing a pending approval request' },
  // Sample documents for the Paperwork tab. `previews` are page renders
  // (public/img/marketing) — previews[0] is used as the thumbnail/main
  // image same as every other shot in this tour, and the lightbox opens
  // page 1 with its own prev/next to flip through the rest for a
  // multi-page document, rather than jumping to a sibling shot. Without
  // `previews`, a plain document-icon tile is shown instead (see
  // isDocumentShot below). The underlying PDFs still live at
  // public/docs/marketing/<file> but are no longer opened directly: an
  // <iframe> PDF viewer proved unreliable across browsers (blank in some,
  // no inline support on iOS Safari), so the preview images are the real
  // experience now, not just a placeholder for it.
  { file: 'rfq-sample.pdf', previews: ['rfq-sample_page-0001.webp', 'rfq-sample_page-0002.webp'], title: 'Carrier RFQ', desc: 'A real RFQ generated straight from a quote — rates, equipment, and load details, ready for a carrier to reply to.', alt: 'Sample carrier RFQ document' },
  { file: 'sales-quote-sample.pdf', previews: ['sales-quote-sample_page-0001.webp', 'sales-quote-sample_page-0002.webp'], title: 'Sales Quote', desc: 'A polished, customer-facing quote your team can send in one click.', alt: 'Sample customer sales quote document' },
  { file: 'bol-sample.pdf', previews: ['bol-sample_page-0001.webp'], title: 'Bill of Lading', desc: 'A complete BOL generated straight from the shipment — no retyping.', alt: 'Sample Bill of Lading document' },
  { file: 'shipping-labels-sample.pdf', previews: ['shipping-labels-sample_page-0001.webp'], title: 'Shipping Labels', desc: 'Barcoded 4"×6" labels, one per physical piece.', alt: 'Sample shipping labels document' },
];

// PRODUCT_TOUR_SHOTS entries backed by a generated PDF rather than a plain
// screenshot — see the preview-image comment above for how these render.
function isDocumentShot(shot) {
  return shot.file.toLowerCase().endsWith('.pdf');
}
// Shown noticeably smaller than the other document previews on desktop — a
// real 4"x6" label is roughly a quarter the linear size of an 8.5"x11" page
// (label area ÷ page area ≈ 0.26, so ~half the width at the same shrink
// factor already applied to the full-page previews), and sizing it the same
// as an RFQ/Sales Quote/BOL preview made it read as a full page, not a label.
function isLabelDocumentShot(shot) {
  return shot.file === 'shipping-labels-sample.pdf';
}
function shotAssetPath(shot) {
  if (isDocumentShot(shot)) {
    return shot.previews?.length ? `/img/marketing/${shot.previews[0]}` : `/docs/marketing/${shot.file}`;
  }
  return `/img/marketing/${shot.file}`;
}

// Full-resolution, single-device screenshots — these carry real alpha
// transparency (rounded device corners), which is why they stay .webp/.png
// end to end rather than ever passing through a JPEG re-encode (JPEG has no
// alpha channel, so a transparent corner would flatten to opaque white).
// Used both by the interactive 3D scene below and its lightbox target.
const DEVICE_SHOTS = [
  { file: 'device-desktop-full.webp', title: 'Desktop', desc: 'Every module, one click away.', alt: 'QuoteHaul running in a desktop browser' },
  { file: 'device-tablet-full.webp', title: 'Tablet', desc: 'Full dashboard, built for touch.', alt: 'QuoteHaul running on a tablet' },
  { file: 'device-phone-full.webp', title: 'Phone', desc: 'Shipment status, right in your pocket.', alt: 'QuoteHaul running on a phone' },
];

// Drives the Interactive Feature Showcase's pill tabs — one entry per tab, in
// tab order. `shots` indexes into PRODUCT_TOUR_SHOTS (the flat source of
// truth for the lightbox's prev/next); a feature with more than one shot
// shows a small thumbnail strip under the main image so a visitor can swap
// which one is enlarged, instead of only ever seeing shots[0]. Document
// Attachments (no dedicated screenshot of its own) is folded into the
// Paperwork tab's copy rather than given an empty-handed tab of its own.
const FEATURE_SPOTLIGHTS = [
  {
    tabLabel: 'Dashboard',
    icon: LayoutDashboard,
    title: 'Live Dashboard',
    shots: [0],
    desc: 'See your whole book of business at a glance — every quote and shipment, its status, and where it stands, updated in real time as your team works.',
  },
  {
    tabLabel: '3D Cargo + AR',
    icon: Box,
    badge: 'AR',
    title: 'Freight Builder & 3D Cargo Visualization',
    shots: [1],
    desc: 'Price the load and visualize exactly how it packs into the truck or trailer in one guided flow, from first quote to final delivery. Drag to rotate, switch equipment types, and see instantly whether it actually fits before you quote it, not after. On supported iOS and Android devices, tap View in AR to place that exact load in your own space at true, real-world scale through your camera.',
  },
  {
    tabLabel: 'Multi-Stop',
    icon: Route,
    title: 'Multi-Stop Loads',
    shots: [2, 3],
    desc: 'Combine several quotes onto one shared truck with multiple pickups and drops — track its own status from booked to delivered, see every shipment\'s cargo color-coded in one 3D view, and pull each stop\'s BOL and labels straight from its linked quote.',
  },
  {
    tabLabel: 'Organizations',
    icon: Building2,
    title: 'Organizations Directory',
    shots: [4],
    desc: 'A saved directory of every shipper, consignee, customer, and carrier — no retyping contact details quote after quote.',
  },
  {
    tabLabel: 'Rate Cards',
    icon: Tags,
    title: 'Negotiated Rate Cards',
    shots: [5],
    desc: 'Save per-lane rates with your carriers so the builder offers your real negotiated price instead of a spot-market guess.',
  },
  {
    tabLabel: 'Paperwork',
    icon: FileText,
    title: 'RFQs & Sales Quotes, Generated Instantly',
    shots: [6, 7, 14, 15, 16, 17],
    desc: 'Send a carrier RFQ or a customer-facing Sales Quote as a polished PDF in one click, straight from the numbers you already built — no retyping into a separate paperwork tool. Once a quote is accepted, generate its Bill of Lading, invoice, and barcoded 4"×6" shipping labels just as fast, and attach any supporting file straight to the shipment or organization record, so everything lives in one place instead of scattered across email.',
  },
  {
    tabLabel: 'Analytics',
    icon: BarChart3,
    title: 'Freight Margin Analytics',
    shots: [8],
    desc: 'See revenue, margin, and volume trends across your team, not just a single quote at a time.',
  },
  {
    tabLabel: 'Shipping Lanes',
    icon: Map,
    title: 'Shipping Lanes Map',
    shots: [9, 10],
    desc: "See your busiest lanes — mapped, not guessed. Every lane you've quoted, plotted across the country. Busier lanes stand out at a glance, colors are yours to customize, and you can export the map as an image or PDF whenever you need it. Click any state for its own inbound/outbound breakdown.",
  },
  {
    tabLabel: 'Team & Admin',
    icon: ShieldCheck,
    title: 'Team Roles & Admin Controls',
    shots: [11, 12, 13],
    desc: 'Invite your team with real seat management, Admin/Manager/Member roles, and admin approval workflows enforced server-side — plus every teammate can track the status of their own requests.',
  },
];

// Grounded in the app's actual gating (src/utils/navItems.js's isPro flags +
// the dual-persistence pattern — guest/demo mode works entirely off
// localStorage, no Firestore sync), not aspirational marketing copy. There's
// no separate paid "free tier" in this app (see the Pricing section above —
// one plan, every feature) — the free side of this comparison is the
// no-signup local demo mode, not a lesser paid plan. Every row's Pro value is
// `true` today (Pro is a strict superset of demo mode), which is what lets
// the "What You Get" cards below render the Pro side generically.
const COMPARISON_ROWS = [
  { label: 'Freight Builder (quote calculator)', demo: 'Sample use', pro: true },
  { label: 'Cargo Visualization (3D layout view + AR on supported devices)', demo: true, pro: true },
  { label: 'Equipment Suggestion (auto-recommends the cheapest fit)', demo: true, pro: true },
  { label: 'Multi-Stop Loads (combine quotes onto one shared truck)', demo: true, pro: true },
  { label: 'Default Rates & Pricing', demo: false, pro: true },
  { label: 'Organizations Directory', demo: 'Browse only, with sample data', pro: true },
  { label: 'Negotiated Rate Cards', demo: 'Browse only, with sample data', pro: true },
  { label: 'BOL, Invoice & Shipping Label Generation', demo: false, pro: true },
  { label: 'Document Attachments', demo: false, pro: true },
  { label: 'Freight Margin Analytics', demo: false, pro: true },
  { label: 'Shipping Lanes Map (US map + state-level drilldown)', demo: false, pro: true },
  { label: 'Team Roles — Admin, Manager & Member', demo: false, pro: true },
];

const FAQS = [
  { q: 'What is QuoteHaul?', a: 'QuoteHaul is freight quoting and shipment management software for brokers, shippers, and carriers — build a quote, price it, generate the paperwork, and track it through delivery.' },
  { q: 'Is QuoteHaul a full TMS?', a: 'No — it\'s a lighter, faster alternative for brokers and shippers who need quoting, 3D cargo planning, and paperwork without the cost or complexity of a full TMS implementation. If you need carrier GPS tracking, EDI, or dispatch, a full TMS is a better fit; if you need to price a load, see how it fits, and get it booked and documented fast, QuoteHaul is built for exactly that.' },
  { q: 'Can I try it before paying anything?', a: `Yes — try the Freight Builder free with no signup at all (local demo mode), or start a ${TRIAL_DAYS}-day free trial of the full Pro plan. Your card isn't charged until the trial ends.` },
  { q: 'What happens after the free trial?', a: `Your subscription renews automatically at the plan price you selected unless you cancel first. You can cancel anytime before the trial ends with no charge.` },
  { q: 'Do I have to start with the free trial?', a: 'No — if you\'d rather skip it, choose "Buy Now" at checkout to start your Pro subscription immediately with no trial period. Same price either way, just no wait.' },
  { q: 'Can I add teammates?', a: 'Yes — invite teammates as additional seats on your subscription, with admin-controlled roles and access.' },
  { q: 'Does the AR cargo view work on every phone?', a: 'AR is available on supported iOS and Android devices with a camera and AR hardware support (Quick Look on iOS, Scene Viewer on Android) — it isn\'t available on desktop, and not every phone or tablet supports it. On iPhone/iPad, Safari loads AR noticeably faster than Chrome, since Quick Look is built directly into Safari. The 3D cargo view itself (drag to rotate, zoom, switch equipment) works everywhere, with or without AR.' },
];

function formatUsd(n) {
  return `$${n}`;
}

// Deliberately its OWN storage key, separate from the app's own
// 'quotehaul_theme' (the one set/saved via a signed-in account's Settings) —
// confirmed bug from sharing that key: App.jsx defaults to 'dark' whenever
// nothing is stored, but its effect immediately persists that default BACK
// to 'quotehaul_theme' on every single app visit, even if the visitor never
// touches the theme toggle in Settings. This page's theme has nothing to do
// with that account-level setting; a dedicated key keeps it fully unrelated.
//
// Split into two pieces of state on purpose, not one:
//  - explicitTheme: only ever set by THIS page's own toggle below, and only
//    then persisted to localStorage. null until the visitor actually clicks it.
//  - systemTheme: the OS's prefers-color-scheme, tracked LIVE via a
//    matchMedia listener (not just read once at mount) so this page keeps
//    following the OS setting in real time — including an OS-level light/dark
//    switch (e.g. an auto night-mode schedule) while the tab is already open.
// Collapsing these into one persisted value (the previous version) meant the
// very first resolved value got written to storage and then replayed on
// every later visit — so it only ever reflected the OS setting once, not
// "the system preferred theme," which is what was actually asked for.
// One Workspace, Every Screen: the desktop/tablet/phone composition's
// reference layout, at the pixel size it was originally designed at
// (1100×610). Every value below is converted to a PERCENTAGE of that
// reference box when rendered, so the exact same relative arrangement
// (desktop centered-back, tablet offset right-mid, phone offset left-front)
// scales down fluidly for phone/tablet viewports instead of needing a
// separate stacked mobile layout — the percentages are identical at every
// breakpoint, only the container's actual pixel size changes.
// `centerOffset` recenters the group: the three devices' combined visual
// footprint isn't symmetric around the desktop's own center (tablet is
// wider than phone), so without this the whole cluster reads as shifted
// right within its section — confirmed by measuring the rendered bounding
// box in a browser (union center ~97px right of the container's actual
// center at 1100px wide).
const SCENE_REF_W = 1100;
const SCENE_REF_H = 610;
const SCENE_CENTER_OFFSET = -97;
function sceneLayout(marginLeft, width, top) {
  return {
    left: `${50 + ((marginLeft + SCENE_CENTER_OFFSET) / SCENE_REF_W) * 100}%`,
    width: `${(width / SCENE_REF_W) * 100}%`,
    top: `${(top / SCENE_REF_H) * 100}%`,
  };
}
const DEVICE_SCENE_LAYOUT = {
  desktop: sceneLayout(-320, 640, 18),
  tablet: sceneLayout(150, 380, 104),
  phone: sceneLayout(-330, 150, 230),
};

function getSystemTheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
}

// Backing data for the Interactive Feature Showcase's active-tab panel.
// Returns a fresh { title, desc, badge, icon, shots } for the given index —
// pulled out of the JSX below since both the tab bar and the panel need it.
function getActiveFeature(idx) {
  return FEATURE_SPOTLIGHTS[idx] ?? FEATURE_SPOTLIGHTS[0];
}

export default function LandingPage() {
  const [explicitTheme, setExplicitTheme] = useState(() => {
    try {
      const stored = localStorage.getItem('quotehaul_landing_theme');
      return stored === 'light' || stored === 'dark' ? stored : null;
    } catch (e) { return null; }
  });
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const theme = explicitTheme || systemTheme;
  const isLight = theme === 'light';

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => setSystemTheme(e.matches ? 'light' : 'dark');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('light', theme === 'light');
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  function toggleTheme() {
    const next = theme === 'light' ? 'dark' : 'light';
    setExplicitTheme(next);
    try { localStorage.setItem('quotehaul_landing_theme', next); } catch (e) {}
  }

  const [billingCycle, setBillingCycle] = useState('monthly');
  // Chosen here on the pricing card and passed straight into TrialFlow below
  // as props — so the plan/seat count a visitor picks BEFORE creating an
  // account is already pre-filled once they reach checkout, instead of
  // asking them to re-pick from scratch right after signup.
  const [extraSeats, setExtraSeats] = useState(0);
  const [legalDoc, setLegalDoc] = useState(null);
  // Whether the feature-showcase lightbox is open — the image shown and
  // prev/next cycling are driven by activeShotWithinFeature below, scoped to
  // just the active feature's own shots, not the full flat shot list.
  const [shotLightboxOpen, setShotLightboxOpen] = useState(false);
  // Which page of a multi-page document (e.g. the 2-page RFQ/Sales Quote
  // sample) is showing inside the lightbox. Reset to 0 every time the
  // lightbox is (re)opened; while it's showing more than one page, prev/next
  // flips between this document's own pages instead of jumping to a sibling
  // shot, since "next" landing on an unrelated document's page 1 would be
  // confusing.
  const [docPageIdx, setDocPageIdx] = useState(0);
  const [expandedDeviceIdx, setExpandedDeviceIdx] = useState(null);
  // Opens the in-page trial flow (plan/seat selection -> account creation ->
  // auto-resume checkout) instead of navigating to /ops/?auth=signup.
  const [trialFlowOpen, setTrialFlowOpen] = useState(false);
  // Floating "back to top" button — only shown once a visitor has actually
  // scrolled past the hero, so it isn't just sitting there uselessly on
  // first load. One scroll listener for the whole page rather than a
  // per-section observer, since the show/hide threshold is a single simple
  // "scrolled past N px" check, not tied to any specific section boundary.
  const [showBackToTop, setShowBackToTop] = useState(false);
  // Once the footer scrolls into view, the button would otherwise sit fixed
  // on top of it (covering Terms/Privacy/social links) since it's positioned
  // relative to the viewport, not the document. Track how far the footer
  // has crept up past the viewport bottom and push the button up by that
  // same amount so it stops right above the footer instead of over it.
  const footerRef = useRef(null);
  const [backToTopOffset, setBackToTopOffset] = useState(24);
  useEffect(() => {
    function handleScroll() {
      setShowBackToTop(window.scrollY > 600);
      const footerEl = footerRef.current;
      const overlap = footerEl ? window.innerHeight - footerEl.getBoundingClientRect().top : 0;
      setBackToTopOffset(overlap > 0 ? overlap + 24 : 24);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);
  // A URL landed on with a #features/#pricing/#faq hash (e.g. a bookmark, or
  // the header nav's own anchor links) doesn't auto-scroll on its own here —
  // the browser's native "jump to #hash on load" behavior fires once at the
  // initial document load, but this page is client-rendered, so the target
  // section doesn't exist in the DOM yet at that moment; the browser has
  // nothing to scroll to and never retries. Doing it ourselves once the real
  // content has mounted fixes it.
  useEffect(() => {
    if (!window.location.hash) return;
    const id = window.location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView();
  }, []);
  // Header "Sign In" link — needs to know whether the visitor already has a
  // session BEFORE deciding where to send them (straight to their dashboard
  // vs. /ops/?auth=login to force the login modal), but this file
  // deliberately has zero Firebase imports at the top level (see this
  // module's own comment on PRODUCT_TOUR_SHOTS/TrialFlow) so the whole SDK
  // never loads for a visitor who's just browsing. Loading it on-click,
  // lazily, the same way TrialFlow.jsx is only pulled in once "Start Your
  // Trial" is actually clicked, keeps that guarantee intact.
  const [checkingSignIn, setCheckingSignIn] = useState(false);
  async function handleSignInClick(e) {
    e.preventDefault();
    if (checkingSignIn) return;
    setCheckingSignIn(true);
    // Deliberate, one-time hand-off of THIS page's current theme into the
    // app's own storage key, only at the moment of this explicit click —
    // NOT a passive sync between the two keys generally, which is exactly
    // what quotehaul_landing_theme was split out from quotehaul_theme to
    // avoid in the first place (see this component's own theme state
    // comment: sharing one key meant simply visiting the app as a guest
    // silently overwrote whatever a landing-page visitor had chosen, since
    // the app's own theme-persist effect always writes something back on
    // every visit). A real, explicit "take me to the app" click is exactly
    // the one moment carrying the choice over is actually wanted.
    try { localStorage.setItem('quotehaul_theme', theme); } catch (e) {}
    try {
      const { auth } = await import('../firebase.js');
      await auth.authStateReady();
      window.location.href = auth.currentUser ? '/ops/' : '/ops/?auth=login';
    } catch (err) {
      // Firebase failed to load or initialize for any reason — fail open to
      // the login-prompt path rather than leaving the link dead.
      window.location.href = '/ops/?auth=login';
    }
  }
  // TrialFlow mounts inline on this same document, so it already inherits
  // this page's live theme with no hand-off needed. But once signup inside
  // it completes, it navigates into the real app (a separate document) —
  // same hand-off as handleSignInClick above, so that eventual page load
  // also picks up the theme the visitor had here.
  function handleStartTrialClick(location) {
    trackTrialCtaClick(location);
    try { localStorage.setItem('quotehaul_theme', theme); } catch (e) {}
    setTrialFlowOpen(true);
  }
  const basePrice = billingCycle === 'annual' ? BASE_ANNUAL : BASE_MONTHLY;
  const seatPrice = billingCycle === 'annual' ? SEAT_ANNUAL : SEAT_MONTHLY;
  const totalPrice = basePrice + seatPrice * extraSeats;

  // ── Interactive Feature Showcase state ──────────────────────────────────
  const [activeFeatureIdx, setActiveFeatureIdx] = useState(1); // 3D Cargo + AR first, it's the headline feature
  const [activeShotWithinFeature, setActiveShotWithinFeature] = useState(0);
  const activeFeature = getActiveFeature(activeFeatureIdx);
  const activeFeatureShots = activeFeature.shots.map((i) => PRODUCT_TOUR_SHOTS[i]);
  const activeShot = activeFeatureShots[activeShotWithinFeature] ?? activeFeatureShots[0];
  // Only set when activeShot actually has more than one page — a
  // single-page document's previews[0] is handled by the normal
  // shotAssetPath path below, same as any other shot.
  const activeShotPages = activeShot.previews?.length > 1 ? activeShot.previews : null;
  // Flattens every shot in the active feature into one ordered page
  // sequence — a single-page shot contributes one entry, a multi-page
  // document (e.g. the 2-page RFQ/Sales Quote samples) contributes one per
  // page — so the lightbox's prev/next can walk continuously from a
  // document's last page straight into the next shot's first page, instead
  // of being stuck cycling within just the one document.
  const featureLightboxPages = activeFeatureShots.flatMap((shot, sIdx) => {
    const pageCount = shot.previews?.length > 1 ? shot.previews.length : 1;
    return Array.from({ length: pageCount }, (_, pIdx) => ({ shotIdx: sIdx, pageIdx: pIdx }));
  });
  function goToLightboxPage(flatIdx) {
    const { shotIdx, pageIdx } = featureLightboxPages[(flatIdx + featureLightboxPages.length) % featureLightboxPages.length];
    setActiveShotWithinFeature(shotIdx);
    setDocPageIdx(pageIdx);
  }
  function selectFeatureTab(idx) {
    setActiveFeatureIdx(idx);
    setActiveShotWithinFeature(0);
  }

  // ── One Workspace, Every Screen: interactive 3D scene state ─────────────
  // Same cursor position read by all three devices, each applying its own
  // multiplier — this is what makes them read as moving independently
  // instead of tilting together as one rigid block. lg+ only (see JSX): the
  // fixed-depth 3D layout doesn't have a meaningful reduced/stacked version
  // at phone/tablet widths, so those get a simpler static fallback instead.
  const [scenePos, setScenePos] = useState({ px: 0, py: 0 });
  function handleSceneMouseMove(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    setScenePos({
      px: (e.clientX - rect.left) / rect.width - 0.5,
      py: (e.clientY - rect.top) / rect.height - 0.5,
    });
  }
  function handleSceneMouseLeave() {
    setScenePos({ px: 0, py: 0 });
  }
  // Desktop deliberately has no entry here — see the comment on its button
  // below for why it stays static instead of tilting like these two.
  const deviceTilt = useMemo(() => ({
    tablet: { y: scenePos.px * 14, x: -scenePos.py * 8 },
    phone: { y: scenePos.px * 22, x: -scenePos.py * 13 },
  }), [scenePos]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      {/* sticky (not fixed) so it stays in normal flow until scrolled to,
          then pins in place — no extra top-padding needed on the content
          below to avoid an initial overlap the way `fixed` would require.
          z-20 keeps it above the hero's own z-10 (see below), so it doesn't
          get covered by the hero's aurora background while scrolling.
          bg-slate-950/80 + backdrop-blur: 80% opacity so it's mostly solid
          but content is still faintly visible/blurred underneath while
          scrolling, rather than a hard, flat bar. */}
      <header className="sticky top-0 z-20 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/80">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
            <img src="/img/quotehaul-logo 600px.webp" alt="QuoteHaul" className="h-7 sm:h-9 w-auto object-contain shrink-0" />
            <div className="min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                <span className="text-base sm:text-lg font-bold text-white leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>QuoteHaul</span>
                <span className="text-slate-500 text-[10px] sm:text-xs leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>by XAYVR Company</span>
              </div>
              <p className="hidden sm:block text-slate-400 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>Smart freight quoting, visualized.</p>
            </div>
          </div>

          {/* In-page anchor links — hidden below sm since the header is
              already tight there (logo + theme toggle + Go to App + CTA), and
              a visitor scrolling on mobile reaches these sections directly
              anyway. scroll-mt-20 on each target section (not here) is what
              keeps the sticky header from covering the section heading when
              the browser jumps to it. */}
          <nav className="hidden sm:flex items-center gap-5 shrink-0">
            <a href="#features" className="text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap">Features</a>
            <a href="#pricing" className="text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap">Pricing</a>
            <a href="#faq" className="text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap">FAQ</a>
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-1.5 sm:p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition shrink-0"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {/* href is the no-JS fallback (goes straight to the login
                prompt); handleSignInClick intercepts the real click to
                check for an existing session first — see its own comment
                above for why that check has to be lazy. Signed-in visitors
                land straight in their dashboard, no modal in the way;
                everyone else gets /ops/?auth=login, which App.jsx's
                authIntent effect turns into an auto-opened login form. */}
            <a
              href="/ops/?auth=login"
              onClick={handleSignInClick}
              aria-disabled={checkingSignIn}
              className="text-xs sm:text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap"
            >
              {checkingSignIn ? 'One moment…' : 'Sign In'}
            </a>
            <button
              type="button"
              onClick={() => handleStartTrialClick('header')}
              className="px-2.5 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs sm:text-sm font-bold rounded-lg shadow-lg shadow-blue-600/20 transition whitespace-nowrap"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      {/* The only section whose colors actually change with `theme` — the
          rest of the page (below) intentionally keeps a single dark
          treatment either way, same as the pre-redesign page did (its theme
          toggle only ever changed this file's own explicitTheme state and
          the app's shared 'quotehaul_theme' handoff — it never had a real
          light-mode stylesheet path for the marketing page itself). Fully
          theming every section below is a bigger follow-up if it's wanted. */}
      <section className={`relative z-10 overflow-hidden ${isLight ? 'bg-slate-50' : 'bg-slate-950'}`}>
        {/* Aurora mesh background — three blurred, slowly-drifting glow
            shapes rather than a flat fill, plus the brand watermark and a
            faint grid for depth. See src/index.css's "Landing page redesign
            animations" block for the drift keyframes (reduced-motion
            aware). */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className={`landing-aurora-1 absolute w-[820px] h-[820px] -left-40 -top-64 rounded-full blur-[10px] ${isLight ? 'bg-blue-200/70' : ''}`} style={!isLight ? { background: 'radial-gradient(circle, rgba(37,99,235,0.55) 0%, rgba(37,99,235,0) 68%)' } : { background: 'radial-gradient(circle, rgba(191,219,254,0.85) 0%, rgba(191,219,254,0) 68%)' }} />
          <div className={`landing-aurora-2 absolute w-[760px] h-[760px] -right-44 -top-44 rounded-full blur-[10px]`} style={!isLight ? { background: 'radial-gradient(circle, rgba(124,58,237,0.5) 0%, rgba(124,58,237,0) 68%)' } : { background: 'radial-gradient(circle, rgba(221,214,254,0.8) 0%, rgba(221,214,254,0) 68%)' }} />
          <div className={`landing-aurora-3 absolute w-[700px] h-[700px] left-[420px] top-[280px] rounded-full blur-[10px]`} style={!isLight ? { background: 'radial-gradient(circle, rgba(79,70,229,0.45) 0%, rgba(79,70,229,0) 70%)' } : { background: 'radial-gradient(circle, rgba(199,210,254,0.7) 0%, rgba(199,210,254,0) 70%)' }} />
          {/* Brand watermark — sits above the aurora AND above the fade
              gradient below (real bug: it used to sit BEFORE the gradient
              in paint order, which fully hid it under that gradient's
              near-opaque bottom stop in light mode — confirmed by
              reordering). Real source asset (not a downsampled copy) since
              this is production, not a design-canvas mockup. */}
          <div className={`absolute inset-0 ${isLight ? 'bg-gradient-to-b from-slate-50/10 via-slate-50/55 to-slate-50' : 'bg-gradient-to-b from-slate-950/15 via-slate-950/55 to-slate-950'}`} />
          <img
            src="/img/quotehaul-logo-icon-3000px.webp"
            alt=""
            className="absolute right-[-40px] top-1/2 -translate-y-1/2 w-[420px] sm:w-[560px] lg:w-[640px] max-w-none"
            style={{ filter: 'saturate(0.8)', opacity: isLight ? 0.4 : 0.34 }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-14 text-center">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-6 ${isLight ? 'bg-blue-600/10 border border-blue-500/30 text-blue-700' : 'bg-blue-600/20 border border-blue-500/40 text-blue-400'}`}>
            <Sparkles className="w-3.5 h-3.5" /> Smart freight quoting, visualized
          </div>
          <h1 className={`text-4xl sm:text-5xl font-extrabold leading-tight mb-5 ${isLight ? 'text-slate-900' : 'text-white'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>
            See it fit before it ships.
          </h1>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto mb-8 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
            QuoteHaul prices the load, shows you exactly how it packs into the truck in 3D — view it in AR on
            supported phones — and generates the BOL, all before a driver ever shows up. Built for freight brokers, 3PLs,
            freight forwarders, and shippers.
          </p>

          {/* Glassmorphic CTA card */}
          <div className={`inline-flex flex-col sm:flex-row items-center gap-4 p-4 sm:p-5 rounded-2xl backdrop-blur-xl shadow-2xl ${isLight ? 'bg-white/70 border border-slate-900/8' : 'bg-slate-900/55 border border-slate-400/18'}`}>
            <button
              type="button"
              onClick={() => handleStartTrialClick('hero')}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-500/25 transition"
            >
              <span>Start Your {TRIAL_DAYS}-Day Free Trial</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="/ops/"
              onClick={trackDemoStart}
              className={`inline-flex items-center gap-2 px-6 py-3.5 font-bold text-sm rounded-xl transition ${isLight ? 'bg-slate-900/5 hover:bg-slate-900/10 border border-slate-900/10 text-slate-900' : 'bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white'}`}
            >
              Try the Demo — No Signup
            </a>
          </div>
          <p className={`text-xs mt-4 ${isLight ? 'text-slate-500' : 'text-slate-500'}`}>Card required to start a trial. No charge until day {TRIAL_DAYS}. Cancel anytime before then.</p>
        </div>

        {/* Capability strip — deliberately NOT usage stats (no real signup/
            quote-volume numbers exist yet for this early-stage product) but
            true, verifiable statements about what the product does, chosen
            to complement rather than repeat the hero paragraph above (which
            already covers 3D + AR + BOL) and tease the feature showcase
            below. */}
        <div className={`relative flex flex-col md:flex-row flex-wrap items-center justify-center gap-4 md:gap-x-10 md:gap-y-3 lg:gap-x-14 px-4 sm:px-6 py-5 border-t backdrop-blur-sm ${isLight ? 'bg-white/70 border-slate-900/8' : 'bg-slate-950/70 border-slate-400/10'}`}>
          {/* Each item's title+desc sit in one <p> (not a flex row) so long
              copy wraps as ordinary text instead of the title and desc
              splitting into two independently-wrapping columns that drift
              out of alignment with each other — confirmed broken on an
              iPhone 16 Pro Max, where "Instant Paperwork" (the longest
              combined title+desc) wrapped its bold title to a second line
              while the desc span stayed pinned beside the first line only.
              No divider bars between items (there used to be one) — with
              three items whose combined width doesn't reliably fit even at
              desktop widths, flex-wrap can leave a divider stranded at the
              end of a wrapped row with no item after it; the gap alone
              separates them cleanly at every width instead. */}
          <p className="text-center">
            <span className={`font-extrabold text-sm sm:text-base ${isLight ? 'text-slate-900' : 'text-white'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>Equipment Suggestion</span>{' '}
            <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>— auto-recommends the best fit</span>
          </p>
          <p className="text-center">
            <span className={`font-extrabold text-sm sm:text-base ${isLight ? 'text-slate-900' : 'text-white'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>3D Cargo Visualization</span>{' '}
            <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>— know it fits before you quote it</span>
          </p>
          <p className="text-center">
            <span className={`font-extrabold text-sm sm:text-base ${isLight ? 'text-slate-900' : 'text-white'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>Instant Paperwork</span>{' '}
            <span className={`text-xs ${isLight ? 'text-slate-500' : 'text-slate-400'}`}>— RFQs & Sales Quotes generated in one click</span>
          </p>
        </div>
      </section>

      {/* ── ONE WORKSPACE, EVERY SCREEN ────────────────────────────────────
          One responsive 3D scene at every breakpoint — desktop/tablet/phone
          sit at real depth via CSS perspective, each bobbing on its own loop,
          and (wherever a mouse is actually hovering) tilting toward the
          cursor at its own rate (see deviceTilt above). DEVICE_SCENE_LAYOUT's
          percentages keep the exact same relative composition at any
          container width, so phone/tablet viewports get the SAME
          arrangement scaled down, not a different stacked layout — a touch
          visitor just never triggers the hover tilt, same as any other
          hover-only affordance on a touch device. */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          One workspace. Every screen.
        </h2>
        <p className="text-sm text-slate-400 max-w-xl mx-auto mb-10 sm:mb-12">
          Quote from your desktop, check a shipment's status from your phone on the dock, walk a tablet through cargo placement in the yard — same real data, everywhere.
        </p>

        <div
          className="relative mx-auto w-full"
          style={{ maxWidth: SCENE_REF_W, aspectRatio: `${SCENE_REF_W} / ${SCENE_REF_H}`, perspective: '1900px', perspectiveOrigin: '50% 35%' }}
          onMouseMove={handleSceneMouseMove}
          onMouseLeave={handleSceneMouseLeave}
        >
          <div className="absolute inset-0" style={{ transformStyle: 'preserve-3d' }}>
            <button
              type="button"
              onClick={() => setExpandedDeviceIdx(0)}
              aria-label="View desktop screenshot full size"
              className="landing-bob-desktop absolute cursor-zoom-in"
              style={{
                ...DEVICE_SCENE_LAYOUT.desktop,
                zIndex: 1,
                filter: 'drop-shadow(0 40px 60px rgba(0,0,0,0.55))',
                // No hover-tilt here (unlike tablet/phone below) — real bug:
                // desktop is by far the largest of the three, so the exact
                // same rotation angle that reads as a subtle tilt on the
                // smaller devices displaces ITS far edges by a lot more in
                // actual pixels (displacement scales with distance from the
                // rotation pivot at element center). That moved desktop's
                // rendered position out from under the cursor by the time a
                // real click landed anywhere but very near its own center —
                // confirmed by comparing its post-hover hit-test box against
                // where clicks actually resolved. Leaving it static makes
                // its whole visible area (outside the genuine tablet/phone
                // overlap already handled above) reliably clickable.
              }}
            >
              {/* borderRadius uses CSS min() (px at real sizes, % as a
                  ceiling) rather than a flat px value — a flat 10-20px
                  radius looked fine at the ~600-900px this was designed at,
                  but at mobile widths these images render as small as
                  ~50px wide (measured), where a fixed 20px radius eats
                  ~40% of the image and reads as the screenshot being
                  cropped, not rounded. */}
              <img src="/img/marketing/device-desktop-full.webp" alt="QuoteHaul on desktop" className="block w-full h-auto" style={{ borderRadius: 'min(10px, 4%)' }} />
            </button>

            {/* Tablet and phone visually overlap a real chunk of the desktop
                shot in this composition (confirmed: ~40-48% of each of
                their own bounding boxes sits on top of desktop's), and
                since each was a single <button> spanning its full image,
                that whole overlapping rectangle silently swallowed clicks
                meant for desktop — a visitor could click what looked like
                empty space near desktop's edge and get the tablet or phone
                lightbox instead, or (from their perspective) "nothing" if
                they'd already given up. Splitting the image (display only,
                pointer-events-none) from a smaller centered hit-button
                (inset 16%, i.e. the middle ~68% of the device) fixes that:
                a click near the natural center of a device still opens
                that device, but the outer edge — which is exactly where
                the overlap with desktop lives — now passes through to
                whatever is actually beneath it instead of eating the click. */}
            <div
              className="landing-bob-tablet absolute"
              style={{
                ...DEVICE_SCENE_LAYOUT.tablet,
                zIndex: 2,
                filter: 'drop-shadow(0 34px 50px rgba(0,0,0,0.55))',
                transform: `translateZ(90px) rotateY(${deviceTilt.tablet.y}deg) rotateX(${deviceTilt.tablet.x}deg)`,
                transition: 'transform 0.2s ease-out',
              }}
            >
              <img src="/img/marketing/device-tablet-full.webp" alt="QuoteHaul on tablet" className="block w-full h-auto pointer-events-none" style={{ borderRadius: 'min(16px, 6%)' }} />
              <button
                type="button"
                onClick={() => setExpandedDeviceIdx(1)}
                aria-label="View tablet screenshot full size"
                className="absolute cursor-zoom-in"
                style={{ inset: '16%' }}
              />
            </div>

            <div
              className="landing-bob-phone absolute"
              style={{
                ...DEVICE_SCENE_LAYOUT.phone,
                zIndex: 3,
                filter: 'drop-shadow(0 30px 44px rgba(0,0,0,0.6))',
                transform: `translateZ(170px) rotateY(${deviceTilt.phone.y}deg) rotateX(${deviceTilt.phone.x}deg)`,
                transition: 'transform 0.15s ease-out',
              }}
            >
              <img src="/img/marketing/device-phone-full.webp" alt="QuoteHaul on phone" className="block w-full h-auto pointer-events-none" style={{ borderRadius: 'min(20px, 10%)' }} />
              <button
                type="button"
                onClick={() => setExpandedDeviceIdx(2)}
                aria-label="View phone screenshot full size"
                className="absolute cursor-zoom-in"
                style={{ inset: '16%' }}
              />
            </div>
          </div>
        </div>
        <p className="hidden md:block text-xs text-slate-600 mt-4">Move your cursor over the scene — each device tilts independently.</p>
      </section>

      {/* ── INTERACTIVE FEATURE SHOWCASE ─────────────────────────────────── */}
      {/* Replaces the old long alternating-row scroll story with pill tabs
          over one active panel — clicking a pill swaps the screenshot +
          copy instead of scrolling past nine stacked rows. scroll-mt-20
          offsets the anchor jump from the header nav's "Features" link. */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 scroll-mt-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Everything a quote needs to become a shipment
        </h2>
        <p className="text-sm text-slate-400 text-center max-w-xl mx-auto mb-10">
          3D cargo visualization, freight analytics across your whole team, and paperwork that writes itself — the tools that take your operation to the next level.
        </p>

        {/* Pill tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-9">
          {FEATURE_SPOTLIGHTS.map((f, idx) => {
            const isActive = idx === activeFeatureIdx;
            return (
              <button
                key={f.title}
                type="button"
                onClick={() => selectFeatureTab(idx)}
                className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition border ${
                  isActive
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-400/50 shadow-lg shadow-blue-600/30'
                    : 'bg-slate-800/40 text-slate-300 border-slate-700/60 hover:bg-slate-800 hover:text-white'
                }`}
              >
                {f.tabLabel}
              </button>
            );
          })}
        </div>

        {/* Active panel */}
        <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12 bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800/80 rounded-3xl p-6 sm:p-10 shadow-2xl">
          <div className="w-full md:w-2/5 min-w-0 text-center md:text-left">
            <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-4 mx-auto md:mx-0">
              <activeFeature.icon className="w-5 h-5 text-blue-400" />
            </div>
            <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-3 flex items-center justify-center md:justify-start gap-2 flex-wrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {activeFeature.title}
              {activeFeature.badge && (
                <span className="px-1.5 py-0.5 rounded-full bg-blue-600/20 border border-blue-500/40 text-blue-400 text-[9px] font-bold uppercase tracking-wider">
                  {activeFeature.badge}
                </span>
              )}
            </h3>
            <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{activeFeature.desc}</p>
          </div>
          <div className="w-full md:w-3/5 min-w-0">
            <button
              type="button"
              onClick={() => { setDocPageIdx(0); setShotLightboxOpen(true); }}
              className="block w-full cursor-zoom-in group"
            >
              {isDocumentShot(activeShot) ? (
                activeShot.previews?.length ? (
                  <div
                    className={`relative rounded-xl overflow-hidden border border-slate-800 shadow-xl bg-white mx-auto transition-transform duration-300 group-hover:-translate-y-1 ${
                      isLabelDocumentShot(activeShot) ? 'w-full md:w-1/4' : 'w-full md:w-1/2'
                    }`}
                  >
                    <img
                      src={`/img/marketing/${activeShot.previews[0]}`}
                      alt={activeShot.alt}
                      loading="lazy"
                      className="w-full h-auto"
                    />
                  </div>
                ) : (
                  <div className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-3 bg-slate-900 rounded-2xl border border-slate-800 shadow-xl transition-transform duration-300 group-hover:-translate-y-1">
                    <FileText className="w-14 h-14 text-blue-400" />
                    <span className="text-sm font-bold text-white">{activeShot.title}</span>
                    <span className="text-xs text-slate-400">Click to view PDF</span>
                  </div>
                )
              ) : (
                <img
                  src={shotAssetPath(activeShot)}
                  alt={activeShot.alt}
                  loading="lazy"
                  className="w-full h-auto drop-shadow-xl transition-transform duration-300 group-hover:-translate-y-1"
                />
              )}
            </button>
            {activeFeatureShots.length > 1 && (
              <div className="flex items-center gap-3 justify-center mt-4">
                {activeFeatureShots.map((shot, sIdx) => (
                  <button
                    key={shot.file}
                    type="button"
                    onClick={() => setActiveShotWithinFeature(sIdx)}
                    className={`${isLabelDocumentShot(shot) ? 'w-10 sm:w-12' : 'w-16 sm:w-20'} rounded-lg overflow-hidden border-2 transition ${sIdx === activeShotWithinFeature ? 'border-blue-500' : 'border-slate-700 opacity-60 hover:opacity-100'}`}
                  >
                    {isDocumentShot(shot) ? (
                      shot.previews?.length ? (
                        <img src={`/img/marketing/${shot.previews[0]}`} alt={shot.alt} loading="lazy" className="w-full h-auto bg-white" />
                      ) : (
                        <div className="w-full aspect-[4/3] flex items-center justify-center bg-slate-900">
                          <FileText className="w-6 h-6 text-blue-400" />
                        </div>
                      )
                    ) : (
                      <img src={shotAssetPath(shot)} alt={shot.alt} loading="lazy" className="w-full h-auto" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-4xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 scroll-mt-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Simple, per-organization pricing
        </h2>
        <p className="text-sm text-slate-400 text-center mb-8">One plan. Every feature. Add seats as your team grows.</p>

        <div className="flex justify-center mb-8">
          <div className="bg-slate-900 p-1 rounded-xl border border-slate-800 flex items-center gap-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                billingCycle === 'monthly' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${
                billingCycle === 'annual' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}
            >
              <span>Annual</span>
              <span className="bg-emerald-500 text-black px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">{ANNUAL_SAVINGS_LABEL}</span>
            </button>
          </div>
        </div>

        {/* Animated glowing gradient border (see .landing-price-glow /
            landingBorderGlow in index.css) makes this card read as the
            page's centerpiece rather than one card among many. */}
        <div className="landing-price-glow rounded-[26px] p-[2px] bg-gradient-to-br from-blue-400/60 via-indigo-400/30 to-blue-400/60">
          <div className="bg-gradient-to-b from-slate-900 to-slate-950 rounded-[24px] p-8 text-center">
            <div className="text-5xl font-extrabold text-white mb-1">
              {formatUsd(basePrice)}<span className="text-lg font-semibold text-slate-400">/mo</span>
            </div>
            <p className="text-xs text-slate-500 mb-6">
              First seat included · additional seats {formatUsd(seatPrice)}/mo each
            </p>

            {/* Seat count chosen here is passed straight into TrialFlow as a
                prop, so it's already pre-filled at checkout — a visitor who
                knows they need e.g. 3 seats doesn't have to re-enter that a
                second time after creating their account. */}
            <div className="flex items-center justify-between gap-3 mb-4 bg-slate-800/60 p-3 rounded-xl border border-slate-700/60 max-w-sm mx-auto">
              <div className="text-left min-w-0">
                <div className="text-xs font-bold text-slate-200">Additional Team Seats</div>
                <div className="text-[11px] text-slate-500">Add more now, or later from Settings</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setExtraSeats(s => Math.max(0, s - 1))}
                  className="w-7 h-7 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span className="w-6 text-center font-bold text-white text-sm">{extraSeats}</span>
                <button
                  type="button"
                  onClick={() => setExtraSeats(s => Math.min(50, s + 1))}
                  className="w-7 h-7 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {extraSeats > 0 && (
              <div className="flex items-center justify-between gap-3 mb-6 px-1 max-w-sm mx-auto">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wide">Total after trial</span>
                <span className="text-lg font-extrabold text-white">{formatUsd(totalPrice)}/mo</span>
              </div>
            )}

            <ul className="text-left max-w-sm mx-auto space-y-2.5 mb-8">
              {[
                'Unlimited Cloud Sync & Quote Versioning',
                'Customer Directory & Vendor/Customer Rate Cards',
                'Default Rate Sheets & Freight Margin Analytics',
                'BOL & Invoice Generation',
                'Team Seats & Admin Controls',
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => handleStartTrialClick('pricing')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-500/25 transition"
            >
              <span>Start Your {TRIAL_DAYS}-Day Free Trial</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ── WHAT YOU GET (Demo vs Pro) ──────────────────────────────────────
          A single unified row list with Demo/Pro icon columns side by side,
          rather than two independently-scrolling cards — on narrow/portrait
          screens the old two-card layout meant scrolling through all of
          Demo's rows before Pro's rows even came into view, making it
          impossible to compare "does Demo have this? does Pro have this?"
          at a glance. One shared row per feature fixes that at every
          breakpoint. "Demo" here means the no-signup local demo mode, not a
          second paid tier — this app only has one paid plan (see Pricing
          above). Every COMPARISON_ROWS entry has pro:true today (Pro is a
          strict superset of demo mode), so the Pro column always renders a
          plain check without a per-row branch. */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          What You Get With Pro
        </h2>
        <p className="text-sm text-slate-400 text-center mb-8 sm:mb-10 max-w-lg mx-auto">
          Try the Freight Builder free with no signup, including the live 3D cargo visualization with AR on supported devices — and
          browse a sample-filled Organizations Directory, Rate Cards, and My Requests too. QuoteHaul Pro unlocks a full team
          workspace: saving your own real data, BOL/invoice/label generation, document attachments, org-wide reporting, and
          role-based team management.
        </p>

        <div className="rounded-2xl bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800/80 px-3 sm:px-8">
          {/* Column header */}
          <div className="flex items-center gap-2 sm:gap-6 py-3 sm:py-4 border-b border-slate-700/60">
            <div className="flex-1" />
            <div className="w-11 sm:w-24 text-center text-xs sm:text-sm font-bold text-slate-500 tracking-wide shrink-0">
              Demo
            </div>
            <div className="w-14 sm:w-36 text-center text-xs sm:text-sm font-extrabold text-blue-400 uppercase tracking-wide leading-tight shrink-0">
              <span className="sm:hidden normal-case" style={{ color: '#fbbf24' }}>Pro</span>
              <span className="hidden sm:inline normal-case" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <span className="font-bold text-white">QuoteHaul</span>
                <span style={{ color: '#fbbf24' }}> Pro</span>
              </span>
            </div>
          </div>

          {COMPARISON_ROWS.map((row, idx) => (
            <div key={idx} className="flex items-center gap-2 sm:gap-6 py-2.5 sm:py-4 border-b border-slate-800/70 last:border-0">
              <div className="flex-1 min-w-0 text-xs sm:text-sm text-slate-200">
                {row.label}
                {typeof row.demo === 'string' && (
                  <span className="text-slate-500 text-[10px] sm:text-xs"> — {row.demo.toLowerCase()}</span>
                )}
              </div>
              <div className="w-11 sm:w-24 flex items-center justify-center shrink-0">
                {row.demo === true && <Check className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-slate-400" />}
                {row.demo === false && <X className="w-3 h-3 sm:w-[15px] sm:h-[15px] text-slate-600" />}
                {typeof row.demo === 'string' && <Circle className="w-2.5 h-2.5 sm:w-[15px] sm:h-[15px] text-slate-500" />}
              </div>
              <div className="w-14 sm:w-36 flex items-center justify-center shrink-0">
                <Check className="w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] text-emerald-400" strokeWidth={3} />
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10">
          <button
            type="button"
            onClick={() => handleStartTrialClick('comparison_table')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-500/25 transition"
          >
            <span>Start Your {TRIAL_DAYS}-Day Free Trial</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 scroll-mt-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {FAQS.map((f, idx) => (
            <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="text-sm font-bold text-white mb-1">{f.q}</div>
              <div className="text-sm text-slate-400">{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer ref={footerRef} className="border-t border-slate-800/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <span>© {new Date().getFullYear()}{' '}
            <a href="https://xayvr.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-300 hover:underline transition">XAYVR Company, LLC</a>
            . All rights reserved.
          </span>
          <div className="flex items-center gap-4">
            <button onClick={() => setLegalDoc('terms')} className="hover:text-slate-300 hover:underline transition">Terms</button>
            <button onClick={() => setLegalDoc('privacy')} className="hover:text-slate-300 hover:underline transition">Privacy</button>
            <a href="/ops/" onClick={trackDemoStart} className="hover:text-slate-300 hover:underline transition">Try the Demo</a>
            {SOCIAL_LINKS.length > 0 && (
              <div className="flex items-center gap-3 pl-3 border-l border-slate-800">
                {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                  <a key={href} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className="text-slate-500 hover:text-slate-300 transition">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </footer>

      <LegalDocModal isOpen={!!legalDoc} onClose={() => setLegalDoc(null)} doc={legalDoc} />

      {shotLightboxOpen && (
        <ImageLightbox
          src={activeShotPages ? `/img/marketing/${activeShotPages[docPageIdx]}` : shotAssetPath(activeShot)}
          alt={activeShot.alt}
          title={activeShotPages ? `${activeShot.title} — Page ${docPageIdx + 1} of ${activeShotPages.length}` : activeShot.title}
          desc={activeShot.desc}
          onClose={() => setShotLightboxOpen(false)}
          onPrev={featureLightboxPages.length > 1 ? () => goToLightboxPage(featureLightboxPages.findIndex(p => p.shotIdx === activeShotWithinFeature && p.pageIdx === docPageIdx) - 1) : undefined}
          onNext={featureLightboxPages.length > 1 ? () => goToLightboxPage(featureLightboxPages.findIndex(p => p.shotIdx === activeShotWithinFeature && p.pageIdx === docPageIdx) + 1) : undefined}
        />
      )}

      {expandedDeviceIdx !== null && (
        <ImageLightbox
          src={`/img/marketing/${DEVICE_SHOTS[expandedDeviceIdx].file}`}
          alt={DEVICE_SHOTS[expandedDeviceIdx].alt}
          title={DEVICE_SHOTS[expandedDeviceIdx].title}
          desc={DEVICE_SHOTS[expandedDeviceIdx].desc}
          onClose={() => setExpandedDeviceIdx(null)}
          onPrev={() => setExpandedDeviceIdx(i => (i - 1 + DEVICE_SHOTS.length) % DEVICE_SHOTS.length)}
          onNext={() => setExpandedDeviceIdx(i => (i + 1) % DEVICE_SHOTS.length)}
        />
      )}

      {trialFlowOpen && (
        <Suspense fallback={
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md">
            <div className="w-8 h-8 border-2 border-slate-600 border-t-blue-500 rounded-full animate-spin" />
          </div>
        }>
          <TrialFlow
            initialBillingCycle={billingCycle}
            initialExtraSeats={extraSeats}
            onClose={() => setTrialFlowOpen(false)}
          />
        </Suspense>
      )}

      {/* Floating back-to-top — same blue as the header/hero CTA buttons
          (bg-blue-600), simple circle + chevron rather than matching their
          gradient, so it reads as a utility control, not another CTA
          competing for attention. z-20 matches the sticky header's own
          stacking context, high enough to sit above section content while
          scrolling. */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => {
            // Clears a lingering #features/#pricing/#faq from the URL —
            // otherwise it stays there after returning to the top, and a
            // page refresh (or anyone copying the URL) would jump right
            // back down instead of landing at the top like the button just
            // did. replaceState (not location.hash = '') so this doesn't
            // itself add a history entry or trigger another scroll jump.
            if (window.location.hash) {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          aria-label="Back to top"
          title="Back to top"
          style={{ bottom: backToTopOffset }}
          className="fixed right-6 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 transition-colors"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
